import lensContract from '~/types/lensContract.json';

import type { BoardOperation } from './editor';
import { type Block, type KpiItem, type LensDocument, type PageEntry, type Row, blockKind, isColumn, isSection, isTabs } from './types';

type Located = { rows: Array<PageEntry>; rowIndex: number; row: Row; itemIndex: number; columnIndex?: number };

export const TREND_FORMS = ['line', 'area', 'column'];

function inRow(row: Row, id: string): { itemIndex: number; columnIndex?: number } | undefined {
  for (const [itemIndex, item] of row.entries()) {
    if (isColumn(item)) {
      const columnIndex = item.findIndex((block) => block.id === id);
      if (columnIndex >= 0) return { itemIndex, columnIndex };
    } else if (item.id === id) return { itemIndex };
  }
  return undefined;
}

function locate(page: Array<PageEntry>, id: string): Located | undefined {
  for (const [index, entry] of page.entries()) {
    if (isSection(entry)) {
      for (const [rowIndex, row] of entry.rows.entries()) {
        const found = inRow(row, id);
        if (found) return { rows: entry.rows, rowIndex, row, ...found };
      }
    } else if (isTabs(entry)) {
      for (const tab of entry.tabs) {
        const found = locate(tab.page, id);
        if (found) return found;
      }
    } else {
      const found = inRow(entry, id);
      if (found) return { rows: page, rowIndex: index, row: entry, ...found };
    }
  }
  return undefined;
}

function blockAt(at: Located): Block {
  const item = at.row[at.itemIndex];
  return isColumn(item) ? item[at.columnIndex!] : item;
}

function replaceAt(at: Located, block: Block) {
  const item = at.row[at.itemIndex];
  if (isColumn(item)) item[at.columnIndex!] = block;
  else at.row[at.itemIndex] = block;
}

function take(page: Array<PageEntry>, id: string): Block | undefined {
  const at = locate(page, id);
  if (!at) return undefined;
  const item = at.row[at.itemIndex];
  let block: Block;
  if (isColumn(item)) {
    block = item.splice(at.columnIndex!, 1)[0];
    if (item.length === 1) at.row[at.itemIndex] = item[0];
    else if (!item.length) at.row.splice(at.itemIndex, 1);
  } else block = at.row.splice(at.itemIndex, 1)[0] as Block;
  if (!at.row.length) at.rows.splice(at.rowIndex, 1);
  return block;
}

function merged(target: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
  const out = { ...target };
  for (const [key, value] of Object.entries(patch)) {
    const nested = value && typeof value === 'object' && !Array.isArray(value) && out[key] && typeof out[key] === 'object' && !Array.isArray(out[key]);
    if (value === null) delete out[key];
    else out[key] = nested ? merged(out[key] as Record<string, unknown>, value as Record<string, unknown>) : value;
  }
  return out;
}

const FIGURE_LOOKS = new Set(['label', 'spark', 'status', 'delta', 'baseline', 'numbers', 'size', 'w']);

function setFigureLook(page: Array<PageEntry>, path: string, patch: Record<string, unknown>): boolean {
  const [blockId, measure] = [path.slice(0, path.indexOf('/')), path.slice(path.indexOf('/') + 1)];
  const at = locate(page, blockId);
  const figures = at && (blockAt(at).kpis as Array<string | KpiItem> | undefined);
  const index = figures?.findIndex((figure) => (typeof figure === 'string' ? figure : figure.kpi) === measure) ?? -1;
  if (!at || !figures || index < 0 || !Object.keys(patch).every((key) => FIGURE_LOOKS.has(key))) return false;
  const figure = figures[index];
  const next = [...figures];
  next[index] = merged(typeof figure === 'string' ? { kpi: figure } : figure, patch) as KpiItem;
  replaceAt(at, { ...blockAt(at), kpis: next });
  return true;
}

const HEADING_WORDS = new Set(['section', 'lead', 'tab']);

function headingOf(page: Array<PageEntry>, id: string): Record<string, unknown> | undefined {
  for (const entry of page) {
    if (isSection(entry) && entry.id === id) return entry as unknown as Record<string, unknown>;
    if (isTabs(entry))
      for (const tab of entry.tabs) {
        if (tab.id === id) return tab as unknown as Record<string, unknown>;
        const inside = headingOf(tab.page, id);
        if (inside) return inside;
      }
  }
  return undefined;
}

function placeBlock(page: Array<PageEntry>, block: Block, anchorId: string, after: boolean, row?: 'new' | 'same'): boolean {
  const at = locate(page, anchorId);
  if (!at) return false;
  const offset = after ? 1 : 0;
  const item = at.row[at.itemIndex];
  if (row !== 'same') at.rows.splice(at.rowIndex + offset, 0, [block]);
  else if (isColumn(item)) item.splice(at.columnIndex! + offset, 0, block);
  else at.row.splice(at.itemIndex + offset, 0, block);
  return true;
}

const SPLIT_FIELDS = ['query', 'where', 'ignore', 'compare', 'window', 'spark'];

export const splitBlockId = (path: string) => path.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '') || 'block';

const figureName = (figure: string | KpiItem) => (typeof figure === 'string' ? figure : figure.kpi);

function figuresAt(page: Array<PageEntry>, path: string) {
  const [blockId, measure] = [path.slice(0, path.indexOf('/')), path.slice(path.indexOf('/') + 1)];
  const at = locate(page, blockId);
  const block = at && blockAt(at);
  const figures = block?.kpis as Array<string | KpiItem> | undefined;
  const index = figures?.findIndex((figure) => figureName(figure) === measure) ?? -1;
  return at && block && figures && index >= 0 ? { at, block, figures, index, measure } : undefined;
}

function moveFigure(page: Array<PageEntry>, path: string, anchorId: string, after: boolean, row?: 'new' | 'same'): boolean {
  const from = figuresAt(page, path);
  if (!from) return false;
  const figure = from.figures[from.index];
  if (anchorId.includes('/')) {
    const to = figuresAt(page, anchorId);
    if (!to || to.measure === from.measure) return false;
    if (to.block !== from.block && (SPLIT_FIELDS.some((key) => JSON.stringify(to.block[key]) !== JSON.stringify(from.block[key])) || to.figures.some((entry) => figureName(entry) === from.measure))) return false;
    const rest = from.figures.filter((_, index) => index !== from.index);
    if (to.block === from.block) {
      rest.splice(rest.findIndex((entry) => figureName(entry) === to.measure) + (after ? 1 : 0), 0, figure);
      replaceAt(from.at, { ...from.block, kpis: rest });
      return true;
    }
    const joined = [...to.figures];
    joined.splice(joined.findIndex((entry) => figureName(entry) === to.measure) + (after ? 1 : 0), 0, figure);
    replaceAt(to.at, { ...to.block, kpis: joined });
    if (rest.length) replaceAt(figuresAt(page, path)!.at, { ...from.block, kpis: rest });
    else take(page, from.block.id);
    return true;
  }
  if (from.figures.length === 1 || !locate(page, anchorId)) return false;
  replaceAt(from.at, { ...from.block, kpis: from.figures.filter((_, index) => index !== from.index) });
  const kept = Object.fromEntries(SPLIT_FIELDS.filter((key) => from.block[key] !== undefined).map((key) => [key, structuredClone(from.block[key])]));
  return placeBlock(page, { id: splitBlockId(path), kpis: [figure], ...kept } as Block, anchorId, after, row);
}

function applyOne(page: Array<PageEntry>, operation: BoardOperation): boolean {
  switch (operation.op) {
    case 'set': {
      const heading = headingOf(page, operation.id);
      if (heading) {
        if (!Object.keys(operation.patch).every((key) => HEADING_WORDS.has(key))) return false;
        for (const [key, value] of Object.entries(operation.patch)) {
          if (value === null) delete heading[key];
          else heading[key] = value;
        }
        return true;
      }
      if (operation.id.includes('/')) return setFigureLook(page, operation.id, operation.patch);
      const at = locate(page, operation.id);
      if (!at) return false;
      replaceAt(at, { ...(merged(blockAt(at), operation.patch) as Block), id: operation.id });
      return true;
    }
    case 'remove':
      return Boolean(take(page, operation.id));
    case 'form': {
      const at = locate(page, operation.id);
      const block = at && blockAt(at);
      const from = block && blockKind(block);
      if (!at || !block || !from || !TREND_FORMS.includes(from) || !TREND_FORMS.includes(operation.kind)) return false;
      const { [from]: measures, ...rest } = block;
      replaceAt(at, { ...rest, [operation.kind]: measures } as Block);
      return true;
    }
    case 'move': {
      const anchorId = operation.after ?? operation.before;
      if (operation.id.includes('/')) return Boolean(anchorId) && moveFigure(page, operation.id, anchorId!, Boolean(operation.after), operation.row);
      if (!anchorId || anchorId === operation.id || !locate(page, anchorId)) return false;
      const block = take(page, operation.id);
      return Boolean(block) && placeBlock(page, block!, anchorId, Boolean(operation.after), operation.row);
    }
    default:
      return false;
  }
}

const PRESENTATION_FIELDS = new Set(lensContract.presentationFields);

const presents = (scope: string, patch: unknown) => Object.keys((patch ?? {}) as object).every((key) => PRESENTATION_FIELDS.has(`${scope}.${key}`));

export function figureMove(operation: BoardOperation): { from: string; to: string } | undefined {
  if (operation.op !== 'move' || !operation.id.includes('/')) return undefined;
  const from = operation.id.split('/')[0];
  const anchor = operation.before ?? operation.after;
  const to = anchor?.includes('/') ? anchor.split('/')[0] : splitBlockId(operation.id);
  return to === from ? undefined : { from, to };
}

export function keepsData(document: LensDocument, operation: BoardOperation): boolean {
  if (operation.op === 'move') return !figureMove(operation);
  if (operation.op === 'remove') return true;
  if (operation.op !== 'set') return false;
  const { id, patch } = operation;
  if (id === 'lens') {
    const { display, ...rest } = patch;
    return presents('lens', rest) && presents('display', display);
  }
  if (id.includes('/')) return presents('item', patch);
  const heading = headingOf(document.page, id);
  if (heading) return presents('section' in heading ? 'section' : 'tab', patch);
  return presents('block', patch);
}

export function applyLocally(document: LensDocument, operations: Array<BoardOperation>): LensDocument | undefined {
  const next = structuredClone(document);
  const applied = operations.every((operation) => {
    if (operation.op !== 'set' || operation.id !== 'lens') return applyOne(next.page, operation);
    Object.assign(next, merged(next as unknown as Record<string, unknown>, (operation as { patch: Record<string, unknown> }).patch));
    if (operation.op === 'set' && operation.patch.lead === null) delete (next as { lead?: string }).lead;
    return true;
  });
  return applied ? next : undefined;
}
